/** @jsx jsx */

import { forwardRef } from 'react';
import { jsx } from '@emotion/core';
import * as colours from '../Style/colours';
import { ALL_TEXT_STYLES, FORM_LABEL_STYLES } from './styles';
import { LabelTextProps, TextProps } from './types';

export const Text = forwardRef<HTMLParagraphElement, TextProps>(
  (props: TextProps, ref) => {
    const {
      size = 'body',
      asTag,
      color: passedColor = 'OL_COLOUR_BLACK_90',
      customMargin: margin,
      padding,
      fontSize,
      fontWeight,
      textAlign,
      marginTop,
      marginBottom,
      customStyle,
      children,
      ...restProps
    } = props;

    let color = passedColor as string;

    if (passedColor in colours) {
      color = colours[passedColor] as string;
    }

    const defaultStyles = ALL_TEXT_STYLES[size];
    let cssStyles = { ...defaultStyles, ...customStyle, color } as Record<
      string,
      string | number
    >;

    if (fontSize) {
      cssStyles = { ...cssStyles, fontSize };
    }
    if (fontWeight) {
      cssStyles = { ...cssStyles, fontWeight };
    }
    if (textAlign) {
      cssStyles = { ...cssStyles, textAlign };
    }

    if (margin) {
      cssStyles = { ...cssStyles, margin };
    }
    if (padding) {
      cssStyles = { ...cssStyles, padding };
    }
    if (marginTop) {
      cssStyles = { ...cssStyles, marginTop };
    }
    if (marginBottom) {
      cssStyles = { ...cssStyles, marginBottom };
    }

    if (size === 'headingLg' || asTag === 'h1') {
      return (
        <h1 ref={ref} css={cssStyles} {...restProps}>
          {children}
        </h1>
      );
    } else if (size === 'headingMd' || asTag === 'h2') {
      return (
        <h2 ref={ref} css={cssStyles} {...restProps}>
          {children}
        </h2>
      );
    } else if (size === 'headingSm' || asTag === 'h3') {
      return (
        <h3 ref={ref} css={cssStyles} {...restProps}>
          {children}
        </h3>
      );
    } else if (size === 'headingXs' || asTag === 'h5') {
      return (
        <h5 ref={ref} css={cssStyles} {...restProps}>
          {children}
        </h5>
      );
    } else if (size === 'subtext' || asTag === 'p') {
      return (
        <p ref={ref} css={cssStyles} {...restProps}>
          {children}
        </p>
      );
    } else if (asTag === 'div') {
      return (
        <div ref={ref} css={cssStyles} {...restProps}>
          {children}
        </div>
      );
    } else if (asTag === 'span') {
      return (
        <span ref={ref} css={cssStyles} {...restProps}>
          {children}
        </span>
      );
    } else if (size === 'formSectionHeading') {
      return (
        <h3 ref={ref} css={cssStyles} {...restProps}>
          {children}
        </h3>
      );
    } else if (size === 'formBodyText') {
      return (
        <p ref={ref} css={cssStyles} {...restProps}>
          {children}
        </p>
      );
    } else if (size === 'formLabelName') {
      return (
        <p ref={ref} css={cssStyles} {...restProps}>
          {children}
        </p>
      );
    } else {
      return (
        <p ref={ref} css={cssStyles} {...restProps}>
          {children}
        </p>
      );
    }
  }
);

/* eslint-disable jsx-a11y/label-has-for */

export const LabelText = forwardRef<HTMLLabelElement, LabelTextProps>(
  (props: LabelTextProps, ref) => {
    const {
      kind = 'formLabelName',
      color: passedColor = 'OL_COLOUR_BLACK_90',
      children,
      customStyle,
      ...restProps
    } = props;

    let color = passedColor as string;

    if (passedColor in colours) {
      color = colours[passedColor] as string;
    }

    const defaultStyles = FORM_LABEL_STYLES[kind];
    const cssStyles = { ...defaultStyles, ...customStyle, color } as Record<
      string,
      string | number
    >;

    if (kind === 'formBodyText') {
      return (
        <label ref={ref} css={cssStyles} {...restProps}>
          {children}
        </label>
      );
    } else {
      return (
        <label ref={ref} css={cssStyles} {...restProps}>
          {children}
        </label>
      );
    }
  }
);
